/**
 * Format Painter Module
 * Copy formatting from selected text and apply it to other text.
 * Single click = one-shot, double click = persistent mode.
 */

let editorEl = null;
let pageRef = null;
let isActive = false;
let isPersistent = false;
let capturedStyles = null;
let painterBtn = null;

const STYLE_PROPS = [
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
    'textDecoration', 'color', 'backgroundColor', 'textAlign'
];

function captureFormatting() {
    const sel = window.getSelection();
    if (!sel.rangeCount) return null;

    let node = sel.anchorNode;
    if (node.nodeType === 3) node = node.parentElement;
    if (!node || !editorEl.contains(node)) return null;

    const computed = window.getComputedStyle(node);
    const styles = {};
    STYLE_PROPS.forEach(prop => {
        styles[prop] = computed[prop];
    });
    return styles;
}

function applyFormatting() {
    if (!capturedStyles) return;

    const sel = window.getSelection();
    if (!sel.rangeCount || sel.isCollapsed) return;

    const range = sel.getRangeAt(0);
    if (!editorEl.contains(range.commonAncestorContainer)) return;

    // Wrap selection in a span with captured styles
    const span = document.createElement('span');
    let styleStr = '';
    STYLE_PROPS.forEach(prop => {
        const val = capturedStyles[prop];
        if (!val) return;
        // Convert camelCase to kebab-case
        const cssProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
        styleStr += `${cssProp}: ${val}; `;
    });
    span.setAttribute('style', styleStr.trim());

    try {
        range.surroundContents(span);
    } catch (e) {
        // If surroundContents fails (partial selection across elements),
        // use execCommand as fallback for key properties
        if (capturedStyles.fontWeight === 'bold' || parseInt(capturedStyles.fontWeight) >= 700) {
            document.execCommand('bold', false, null);
        }
        if (capturedStyles.fontStyle === 'italic') {
            document.execCommand('italic', false, null);
        }
        if (capturedStyles.textDecoration?.includes('underline')) {
            document.execCommand('underline', false, null);
        }
        if (capturedStyles.color) {
            document.execCommand('foreColor', false, capturedStyles.color);
        }
        if (capturedStyles.backgroundColor && capturedStyles.backgroundColor !== 'rgba(0, 0, 0, 0)') {
            document.execCommand('hiliteColor', false, capturedStyles.backgroundColor);
        }
    }

    markDirty();

    if (!isPersistent) {
        deactivate();
    }
}

function activate(persistent) {
    capturedStyles = captureFormatting();
    if (!capturedStyles) return;

    isActive = true;
    isPersistent = persistent;
    editorEl.classList.add('format-painter-active');
    if (painterBtn) painterBtn.classList.add('active');

    editorEl.addEventListener('mouseup', onEditorMouseUp);
}

function deactivate() {
    isActive = false;
    isPersistent = false;
    capturedStyles = null;
    editorEl.classList.remove('format-painter-active');
    if (painterBtn) painterBtn.classList.remove('active');

    editorEl.removeEventListener('mouseup', onEditorMouseUp);
}

function onEditorMouseUp(e) {
    if (!isActive || !capturedStyles) return;
    // Small delay to let selection finalize
    setTimeout(() => applyFormatting(), 10);
}

function onKeydown(e) {
    if (e.key === 'Escape' && isActive) {
        deactivate();
    }
}

function markDirty() {
    if (pageRef) {
        pageRef.blogDirty = true;
        if (pageRef.activeBlogPost) {
            pageRef.activeBlogPost.content_html = editorEl.innerHTML;
        }
        if (pageRef.renderBlogDirtyUI) pageRef.renderBlogDirtyUI();
    }
}

// Public API
export function toggle(persistent) {
    if (isActive) {
        deactivate();
    } else {
        activate(persistent);
    }
}

export function init(editor, page) {
    editorEl = editor;
    pageRef = page;

    // Find the format painter button
    painterBtn = editor.closest('.blog-card')?.querySelector('[data-cmd="formatPainter"]');

    if (painterBtn) {
        let clickCount = 0;
        let clickTimer = null;

        painterBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            clickCount++;
            if (clickCount === 1) {
                clickTimer = setTimeout(() => {
                    // Single click — one-shot
                    toggle(false);
                    clickCount = 0;
                }, 250);
            } else if (clickCount === 2) {
                clearTimeout(clickTimer);
                // Double click — persistent
                if (isActive && !isPersistent) {
                    // Upgrade to persistent
                    isPersistent = true;
                } else {
                    toggle(true);
                }
                clickCount = 0;
            }
        });
    }

    editor.addEventListener('keydown', onKeydown);
}
